import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ImageIcon, ArrowRight, Layers } from 'lucide-react';
import { fetchGallery } from '@/services/api';

interface GalleryImage {
    id: number;
    title: string;
    image: string;
    slug: string;
    thumbnail: string;
    images_count: number; // Added to show album depth
}

const GalleryPreview = () => {
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchHomeGallery = async () => {
            try {
                const response = await fetchGallery();
                const data = response.data;
                
                const galleryItems = data.slice(0, 6).map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    images_count: item.images?.length || 0,
                    thumbnail: item.thumbnail || 'https://res.cloudinary.com/dwrqyrm84/image/upload/q_auto,f_auto/v1747853348/ulqyzwzdazyrxpderkzr.jpg',
                }));

                setImages(galleryItems);
            } catch (error) {
                console.error('Failed to fetch gallery:', error);
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchHomeGallery();
    }, []);

    return (
        <section className="py-24 bg-white relative overflow-hidden">
            {/* Soft decorative background text */}
            <div className="absolute top-10 right-[-5%] text-[12rem] font-serif font-bold text-slate-50 select-none leading-none z-0">
                Gallery
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                
                {/* Header Section */}
                <div className="flex flex-col md:flex-row justify-between items-end mb-16 gap-6">
                    <div className="max-w-xl">
                        <div className="flex items-center gap-3 text-amber-600 mb-4">
                            <div className="h-px w-8 bg-amber-500" />
                            <span className="text-[10px] font-bold uppercase tracking-[0.3em]">Visual Archives</span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900">
                            Diocesan Life <span className="text-slate-400 font-serif italic">in Frames</span>
                        </h2>
                    </div>
                    
                    <Link to="/gallery">
                        <motion.button 
                            whileHover={{ x: 5 }}
                            className="group flex items-center gap-2 text-slate-900 font-bold uppercase tracking-widest text-[10px] border-b-2 border-amber-500 pb-1"
                        >
                            Explore All Albums <ArrowRight size={14} className="text-amber-500" />
                        </motion.button>
                    </Link>
                </div>

                {loading ? (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {[1, 2, 3, 4, 5, 6].map(i => (
                            <div key={i} className="aspect-square bg-slate-100 animate-pulse rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                        {images.map((image, index) => (
                            <motion.div
                                key={image.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                            >
                                <Link to={`/gallery/${image.id}`} className="group relative block">
                                    <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] shadow-xl shadow-slate-200/50 bg-slate-100">
                                        {/* Image */}
                                        <img
                                            src={image.thumbnail}
                                            alt={image.title}
                                            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110 grayscale-[30%] group-hover:grayscale-0"
                                        />

                                        {/* Overlay - Appears on Hover */}
                                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-8">
                                            <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                                                <div className="flex items-center gap-2 text-amber-400 mb-2">
                                                    <Layers size={14} />
                                                    <span className="text-[10px] font-bold uppercase tracking-widest">
                                                        {image.images_count} Photos
                                                    </span>
                                                </div>
                                                <h3 className="text-xl font-serif font-bold text-white leading-tight">
                                                    {image.title}
                                                </h3>
                                            </div>
                                        </div>

                                        {/* Subtle Border Overlay */}
                                        <div className="absolute inset-4 border border-white/20 rounded-[1.5rem] pointer-events-none group-hover:inset-6 transition-all duration-500" />
                                    </div>
                                    
                                    {/* Mobile/Default Title (visible below card on mobile, hidden on group hover if you prefer) */}
                                    <div className="mt-4 px-2 sm:hidden">
                                        <h3 className="text-slate-800 font-serif font-bold truncate">{image.title}</h3>
                                        <p className="text-amber-600 text-[10px] font-bold uppercase">{image.images_count} Photos</p>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}

                {/* Bottom Decorative Element */}
                <div className="mt-20 flex justify-center">
                    <div className="flex items-center gap-4 opacity-20">
                        <div className="h-px w-24 bg-slate-400" />
                        <ImageIcon size={20} className="text-slate-400" />
                        <div className="h-px w-24 bg-slate-400" />
                    </div>
                </div>
            </div>
        </section>
    );
};

export default GalleryPreview;